// Detection-first PDF dispatch (Phase 1b).
//
// Replaces App.tsx's old "try OFW → on MalformedOFWError fall back to Civil
// Communicator" trial-parse misroute (spec §13: never silently route an
// unrecognized export to a wrong parser). The flow is now detection-first
// (spec §3): extract page text ONCE, build a structural sample, classify via
// the Phase-1a scored registry, and route by tier:
//
//   auto    (high score, clear leader) → parse straight into that adapter
//   confirm (low score / top-two within margin) → ask the user which format
//   recover (nothing scored)           → explicit "we couldn't read this" state
//
// This module is the pure, deterministic decision layer — no pdf.js, no React,
// no DOM — so the branch logic is unit-testable on synthetic page text. The
// caller (runPdfIngest in App.tsx) owns the side effects (pdf.js extraction,
// React state, the confirm/recover UI). Lockstep-adjacent with the Python
// registry/contract; the scoring itself lives in _fingerprint / _registry.

import { buildPdfSample, type Sample } from "./_contract";
import {
  classify,
  poll,
  type Candidate,
  type Classification,
  type Tier,
} from "./_registry";
import {
  ingestCivilCommunicatorFromPages,
  type IngestResult as CcIngestResult,
} from "./civil_communicator";
import { ingestOfwFromPages, type IngestResult as OfwIngestResult } from "./ofw";
import {
  ingestTalkingParentsFromPages,
  type IngestResult as TalkingParentsIngestResult,
} from "./talking_parents";
import {
  ingestTwoHousesFromPages,
  type IngestResult as TwoHousesIngestResult,
} from "./two_houses";
import {
  ingestAppCloseFromPages,
  type IngestResult as AppCloseIngestResult,
} from "./appclose";
import type { PositionedLine } from "./_pdf";

// The PDF adapter keys the registry can route to. Generic-CSV is a CSV-only
// adapter (its identify() returns 0 on a PDF sample), so it never appears here;
// CSV ingest is a separate path (unchanged in Phase 1b).
export type PdfFormat =
  | "ofw"
  | "civil-communicator"
  | "two-houses"
  | "talking-parents"
  | "appclose";

// The App's adapter discriminator each PDF format commits under.
export type PdfAdapter =
  | "ofw"
  | "civil_communicator"
  | "two_houses"
  | "talking_parents"
  | "appclose";

// Map the registry's stable format key to the App's adapter discriminator and
// a human label (no tech/domain jargon — never "adapter"/"parser"/the error
// class names). The label is what the confirm step and the done summary show.
export const PDF_FORMAT_INFO: Record<PdfFormat, { adapter: PdfAdapter; label: string }> = {
  ofw: { adapter: "ofw", label: "OurFamilyWizard" },
  "civil-communicator": { adapter: "civil_communicator", label: "Civil Communicator" },
  "two-houses": { adapter: "two_houses", label: "2houses" },
  "talking-parents": { adapter: "talking_parents", label: "TalkingParents" },
  appclose: { adapter: "appclose", label: "AppClose" },
};

/**
 * The same human labels, keyed by the App's adapter discriminator and DERIVED
 * from PDF_FORMAT_INFO rather than restated.
 *
 * The done summary used to choose its label with a hand-maintained ternary
 * ladder, so adding a format silently mislabelled it: an AppClose ingest
 * reported "Generic CSV" — the ladder's CSV fallback — because nobody
 * remembered the ladder was an enumeration too. Anything needing a label for
 * an adapter should read it from here.
 */
export const PDF_ADAPTER_LABEL: Record<PdfAdapter, string> = Object.fromEntries(
  (Object.keys(PDF_FORMAT_INFO) as PdfFormat[]).map((f) => [
    PDF_FORMAT_INFO[f].adapter,
    PDF_FORMAT_INFO[f].label,
  ]),
) as Record<PdfAdapter, string>;

/** Registry keys that this PDF path knows how to parse, in display order. */
export const KNOWN_PDF_FORMATS: PdfFormat[] = [
  "ofw",
  "civil-communicator",
  "two-houses",
  "talking-parents",
  "appclose",
];

function isPdfFormat(name: string | null | undefined): name is PdfFormat {
  return (
    name === "ofw" ||
    name === "civil-communicator" ||
    name === "two-houses" ||
    name === "talking-parents" ||
    name === "appclose"
  );
}

export interface PdfIngestOptions {
  corpusId: string;
  /**
   * FALLBACK zone for formats that can't resolve one from the export
   * itself. Per-adapter semantics (all fallback-shaped — this option must
   * never override a zone the export declares):
   *   - ofw: used only for cover-less exports (cover's Timezone: wins);
   *   - two-houses: used because the export never declares a zone;
   *   - talking-parents: used only when the record's declared zone label
   *     fails to map (the certified label always wins when it resolves);
   *   - civil-communicator: ignored (rows carry their own zone).
   */
  defaultTimezone?: string;
  /**
   * Positioned lines per page, required ONLY by appclose.
   *
   * Every other adapter parses line text; AppClose dispatches on column
   * position, because a wrapped record-header fragment is textually
   * indistinguishable from a new record (112 misattributed senders when done
   * line-wise). `extractPdfPages` returns these alongside the page text in
   * one pass, so supplying them costs no extra extraction.
   */
  lines?: { pageNumber: number; lines: PositionedLine[] }[];
  /**
   * The zone the user assigned. Distinct from `defaultTimezone`: AppClose
   * declares NO zone anywhere, so this is not a fallback for a missing label
   * but the only zone the export can ever have. Without it AppClose accepts
   * nothing and reports needs_timezone.
   */
  assignedTimezone?: string | null;
}

/** A parsed, ready-to-commit result plus which format produced it. */
export interface ParsedPdf {
  format: PdfFormat;
  result:
    | OfwIngestResult
    | CcIngestResult
    | TwoHousesIngestResult
    | TalkingParentsIngestResult
    | AppCloseIngestResult;
}

/**
 * Run the chosen format's deterministic parser over already-extracted pages.
 * Used by both the auto path (the registry picked the format) and the confirm
 * path (the user picked it). Throws the adapter's own malformed-format error —
 * the caller decides whether that becomes a recover state.
 */
export function ingestPdfByFormat(
  pages: string[],
  format: PdfFormat,
  opts: PdfIngestOptions,
): ParsedPdf {
  if (format === "ofw") {
    return {
      format,
      result: ingestOfwFromPages(pages, {
        corpusId: opts.corpusId,
        defaultTimezone: opts.defaultTimezone,
      }),
    };
  }
  if (format === "two-houses") {
    return {
      format,
      result: ingestTwoHousesFromPages(pages, {
        corpusId: opts.corpusId,
        defaultTimezone: opts.defaultTimezone,
      }),
    };
  }
  if (format === "talking-parents") {
    return {
      format,
      result: ingestTalkingParentsFromPages(pages, {
        corpusId: opts.corpusId,
        defaultTimezone: opts.defaultTimezone,
      }),
    };
  }
  if (format === "appclose") {
    if (!opts.lines) {
      // A programming error, not a bad file: AppClose cannot be parsed from
      // page text. Fail loudly rather than silently producing zero records.
      throw new Error(
        "AppClose ingestion requires positioned lines — pass opts.lines from extractPdfPages().",
      );
    }
    return {
      format,
      result: ingestAppCloseFromPages(opts.lines, {
        corpusId: opts.corpusId,
        timezone: opts.assignedTimezone ?? null,
      }),
    };
  }
  return {
    format,
    result: ingestCivilCommunicatorFromPages(pages, { corpusId: opts.corpusId }),
  };
}

/** A confirm-step format option: stable key + human label + whether it's the guess. */
export interface FormatChoice {
  format: PdfFormat;
  label: string;
  /** True for the detector's top guess (rendered with the "Aurora's guess" tag). */
  isGuess: boolean;
}

/**
 * The decision the UI acts on. Detection is decoupled from parsing: an
 * `auto` decision carries the already-parsed result; `confirm` carries the
 * choices the user picks from (parsing happens after they confirm). `recover`
 * carries no choices — it's an honest handoff, not a picker (which format an
 * export is is detection's job). The raw classification is included for
 * diagnostics/tests, never surfaced to the user as-is.
 */
export type PdfRouteDecision =
  | { tier: "auto"; format: PdfFormat; parsed: ParsedPdf; classification: Classification }
  | { tier: "confirm"; choices: FormatChoice[]; guess: PdfFormat | null; classification: Classification }
  | {
      tier: "recover";
      classification: Classification;
      // Recover-only: set when a confidently-detected format was tried and its
      // parse threw (vs nothing recognized). Lets the UI say why instead of a
      // silent drop. Absent for a nothing-scored recover.
      note?: string;
    };

/**
 * Build the ordered confirm-step choices. The detector's guess (best candidate,
 * if it's a known PDF format) leads and is tagged; the runner-up and any other
 * known PDF formats follow as selectable alternatives. On `recover` there is no
 * guess, so every known PDF format is offered for a manual pick.
 */
function buildChoices(best: Candidate | null): { choices: FormatChoice[]; guess: PdfFormat | null } {
  const guess = isPdfFormat(best?.name) ? (best!.name as PdfFormat) : null;
  // Guess first, then the remaining known formats in their canonical order.
  const ordered: PdfFormat[] = guess
    ? [guess, ...KNOWN_PDF_FORMATS.filter((f) => f !== guess)]
    : [...KNOWN_PDF_FORMATS];
  const choices = ordered.map((format) => ({
    format,
    label: PDF_FORMAT_INFO[format].label,
    isGuess: format === guess,
  }));
  return { choices, guess };
}

/**
 * Detection-first routing over already-extracted page text. The single
 * decision point that replaced the trial-parse fallback.
 *
 * - `auto`: the registry has a confident, clear leader → parse it now and
 *   return the result. If that parse throws (a detected-but-unreadable file),
 *   we do NOT fall back to another parser; we surface `recover` so the user
 *   gets the explicit "we couldn't read this" state, never a wrong-parser
 *   crash.
 * - `confirm`: low confidence or two formats too close → return the choices
 *   (guess tagged) for the confirm step; parse happens on the user's pick.
 * - `recover`: nothing scored → return the manual-pick choices.
 */
export function decidePdfRoute(
  pages: string[],
  opts: PdfIngestOptions,
  buildSample: (pages: string[]) => Sample = (p) => buildPdfSample(p),
): PdfRouteDecision {
  const sample = buildSample(pages);
  const candidates = poll(sample);
  const classification = classify(candidates);
  const tier: Tier = classification.tier;

  if (tier === "auto" && isPdfFormat(classification.format)) {
    const format = classification.format;
    try {
      const parsed = ingestPdfByFormat(pages, format, opts);
      return { tier: "auto", format, parsed, classification };
    } catch (err) {
      // Detected confidently but the deterministic parse failed (a variant we
      // recognize the shape of but can't yet read). Don't trial-parse another
      // format — surface recover with a manual pick + say why (never a silent
      // drop). (Phase 4 adds the CTA + scrub; here we just avoid the dead-end /
      // wrong-parser crash.)
      console.error(`PDF auto-parse failed as ${format}:`, err);
      // The parser's own reason rides along: the adapters say exactly what
      // they refused (an unreadable cover, two headers at one position, too
      // many malformed messages), and that line is what support needs.
      const reason = err instanceof Error ? err.message : String(err);
      return {
        tier: "recover",
        classification,
        note:
          `Aurora recognized this as ${PDF_FORMAT_INFO[format].label} but couldn't read it.` +
          (reason ? ` The parser said: ${reason}` : ""),
      };
    }
  }

  if (tier === "confirm") {
    const { choices, guess } = buildChoices(classification.best);
    return { tier: "confirm", choices, guess, classification };
  }

  // recover (nothing scored) — or an "auto" leader that wasn't a PDF format
  // (shouldn't happen: only PDF adapters score on a PDF sample), treated as
  // recover so we never route to a non-PDF parser.
  return { tier: "recover", classification };
}
